// Dourado vocabulary pillar — themed, CEFR-tiered, dual-coded (emoji) word cards.
//
// This is the NEW richer vocabulary dataset that backs the `vocabulary`
// PocketBase collection and the Vocabulary browse view. It is distinct from the
// small legacy `VOCABULARY` array in ./index.js (shape { pt, en, cat }) that the
// old nouns/adjectives/flashcards views still read — that one is re-exported
// below for backward compatibility.
//
// Words and their meanings are language facts; the theme grouping, CEFR tiering,
// emoji pairing, and every example sentence are authored originally for Dourado.
//
// Each row is a tuple kept terse on purpose so the data reads like a table:
//   [ portuguese, english, emoji, part_of_speech, gender, example_pt, example_en ]
// gender is "" for anything that isn't a noun (the builder stores it as "none").

export { VOCABULARY } from "./index.js";

// Net-new, collision-free per-theme cards live in standalone vocab-<theme>.js
// modules and are folded in below via buildVocabulary().
import { EXTRA_THEME_BLOCKS } from "./vocab-expansion.js";

const THEMES = [
  // ---- Identity --------------------------------------------------------
  {
    theme: "identity", tier: "A1", rows: [
      ["o nome", "name", "🪪", "noun", "m", "O meu nome é Ana.", "My name is Ana."],
      ["o apelido", "surname", "🔤", "noun", "m", "Qual é o teu apelido?", "What is your surname?"],
      ["a idade", "age", "🎂", "noun", "f", "Tenho trinta anos de idade.", "I am thirty years of age."],
      ["o país", "country", "🌍", "noun", "m", "Portugal é um país bonito.", "Portugal is a beautiful country."],
      ["a nacionalidade", "nationality", "🛂", "noun", "f", "A minha nacionalidade é norueguesa.", "My nationality is Norwegian."],
      ["a morada", "address", "🏡", "noun", "f", "Dás-me a tua morada?", "Will you give me your address?"],
      ["o telefone", "phone", "📞", "noun", "m", "Não tenho o teu telefone.", "I don't have your phone number."],
      ["o e-mail", "email", "📧", "noun", "m", "Envio-te um e-mail.", "I'll send you an email."],
      ["solteiro", "single", "🙋", "adjective", "", "O meu irmão é solteiro.", "My brother is single."],
      ["casado", "married", "💑", "adjective", "", "Estão casados desde maio.", "They have been married since May."],
    ],
  },
  {
    theme: "identity", tier: "A2", rows: [
      ["o documento", "document", "📄", "noun", "m", "Preciso de assinar o documento.", "I need to sign the document."],
      ["a assinatura", "signature", "✍️", "noun", "f", "Falta a tua assinatura aqui.", "Your signature is missing here."],
      ["o cidadão", "citizen", "🧑", "noun", "m", "É cidadão português.", "He is a Portuguese citizen."],
      ["a profissão", "profession", "💼", "noun", "f", "Qual é a tua profissão?", "What is your profession?"],
      ["o estado civil", "marital status", "💍", "phrase", "", "Indique o seu estado civil.", "State your marital status."],
      ["nascer", "to be born", "👶", "verb", "", "Nasci em Stavanger.", "I was born in Stavanger."],
      ["reformado", "retired", "🧓", "adjective", "", "O meu pai está reformado.", "My father is retired."],
      ["o cartão de cidadão", "ID card", "🪪", "noun", "m", "Mostra o teu cartão de cidadão, por favor.", "Show your ID card, please."],
      ["o sexo", "sex", "⚧️", "noun", "m", "O formulário pergunta o sexo.", "The form asks for sex."],
      ["maior de idade", "of legal age", "🔞", "phrase", "", "Já é maior de idade.", "She is already of legal age."],
    ],
  },

  {
    theme: "identity", tier: "B1", rows: [
      ["a origem", "origin", "🌐", "noun", "f", "As suas origens são humildes.", "His origins are humble."],
      ["a identidade", "identity", "🆔", "noun", "f", "Protege a tua identidade online.", "Protect your identity online."],
      ["o parentesco", "kinship", "🧬", "noun", "m", "Não temos nenhum parentesco.", "We have no family relationship."],
      ["a herança", "heritage", "🏺", "noun", "f", "A herança cultural é rica.", "The cultural heritage is rich."],
      ["pertencer", "to belong", "🧩", "verb", "", "Pertenço a este clube.", "I belong to this club."],
      ["a ascendência", "ancestry", "🌳", "noun", "f", "Tem ascendência italiana.", "She has Italian ancestry."],
      ["o traço", "trait", "🎭", "noun", "m", "A generosidade é o seu melhor traço.", "Generosity is his best trait."],
      ["a personalidade", "personality", "✨", "noun", "f", "Tem uma personalidade forte.", "She has a strong personality."],
      ["o costume", "custom", "🔄", "noun", "m", "É um costume de família.", "It's a family custom."],
      ["enraizado", "deep-rooted", "🌱", "adjective", "", "É um valor muito enraizado.", "It's a deeply rooted value."],
    ],
  },

  // ---- Family ----------------------------------------------------------
  {
    theme: "family", tier: "A1", rows: [
      ["a família", "family", "👪", "noun", "f", "A minha família é grande.", "My family is big."],
      ["o pai", "father", "👨", "noun", "m", "O meu pai cozinha muito bem.", "My father cooks very well."],
      ["a mãe", "mother", "👩", "noun", "f", "Telefono à minha mãe todos os dias.", "I call my mother every day."],
      ["o filho", "son", "👦", "noun", "m", "O filho dela tem cinco anos.", "Her son is five years old."],
      ["a filha", "daughter", "👧", "noun", "f", "A minha filha estuda música.", "My daughter studies music."],
      ["o irmão", "brother", "🧑", "noun", "m", "Tenho um irmão mais novo.", "I have a younger brother."],
      ["a irmã", "sister", "👭", "noun", "f", "A minha irmã vive em Madrid.", "My sister lives in Madrid."],
      ["o avô", "grandfather", "👴", "noun", "m", "O avô conta histórias.", "Grandfather tells stories."],
      ["a avó", "grandmother", "👵", "noun", "f", "A avó faz um bolo.", "Grandmother makes a cake."],
      ["o bebé", "baby", "👶", "noun", "m", "O bebé dorme agora.", "The baby is sleeping now."],
    ],
  },
  {
    theme: "family", tier: "A2", rows: [
      ["o tio", "uncle", "🧔", "noun", "m", "O meu tio vive no México.", "My uncle lives in Mexico."],
      ["a tia", "aunt", "👩", "noun", "f", "A minha tia é médica.", "My aunt is a doctor."],
      ["o primo", "cousin", "🧑", "noun", "m", "O meu primo joga futebol.", "My cousin plays football."],
      ["o sobrinho", "nephew", "👦", "noun", "m", "Tomo conta do meu sobrinho às segundas.", "I look after my nephew on Mondays."],
      ["o neto", "grandson", "👶", "noun", "m", "O neto dele acabou de nascer.", "Her grandson has just been born."],
      ["o esposo", "husband", "🤵", "noun", "m", "O esposo dela trabalha fora.", "Her husband works away."],
      ["a esposa", "wife", "👰", "noun", "f", "A minha esposa fala três línguas.", "My wife speaks three languages."],
      ["o sogro", "father-in-law", "👴", "noun", "m", "O meu sogro é muito amável.", "My father-in-law is very kind."],
      ["o cunhado", "brother-in-law", "🧔", "noun", "m", "O meu cunhado é engenheiro.", "My brother-in-law is an engineer."],
      ["o genro", "son-in-law", "🤵", "noun", "m", "O genro dela cozinha aos domingos.", "Her son-in-law cooks on Sundays."],
    ],
  },

  {
    theme: "family", tier: "B1", rows: [
      ["o antepassado", "ancestor", "🪦", "noun", "m", "Os seus antepassados eram marinheiros.", "His ancestors were sailors."],
      ["a convivência", "coexistence", "🤝", "noun", "f", "A convivência melhora com respeito.", "Living together improves with respect."],
      ["o vínculo", "bond", "🔗", "noun", "m", "Há um vínculo forte entre eles.", "There's a strong bond between them."],
      ["criar", "to raise", "🍼", "verb", "", "Criam os filhos com carinho.", "They raise their children with love."],
      ["a criação", "upbringing", "👐", "noun", "f", "A criação exige paciência.", "Upbringing requires patience."],
      ["adotar", "to adopt", "🤱", "verb", "", "Decidiram adotar uma criança.", "They decided to adopt a child."],
      ["a madrinha", "godmother", "👼", "noun", "f", "A minha madrinha vive perto.", "My godmother lives nearby."],
      ["herdar", "to inherit", "📜", "verb", "", "Herdou a casa da avó.", "He inherited his grandmother's house."],
      ["o parente", "relative", "👪", "noun", "m", "Tenho parentes no Chile.", "I have relatives in Chile."],
      ["unido", "close-knit", "❤️", "adjective", "", "Somos uma família muito unida.", "We are a very close-knit family."],
    ],
  },

  // ---- House & Home ----------------------------------------------------
  {
    theme: "house", tier: "A1", rows: [
      ["a casa", "house", "🏠", "noun", "f", "Vivo numa casa pequena.", "I live in a small house."],
      ["o apartamento", "flat", "🏢", "noun", "m", "O apartamento dela fica no centro.", "Her flat is in the centre."],
      ["a cozinha", "kitchen", "🍳", "noun", "f", "A cozinha é muito luminosa.", "The kitchen is very bright."],
      ["a casa de banho", "bathroom", "🚿", "noun", "f", "A casa de banho fica ao fundo.", "The bathroom is at the back."],
      ["a cama", "bed", "🛏️", "noun", "f", "A cama é muito confortável.", "The bed is very comfortable."],
      ["a porta", "door", "🚪", "noun", "f", "Fecha a porta, por favor.", "Close the door, please."],
      ["a janela", "window", "🪟", "noun", "f", "Abro a janela de manhã.", "I open the window in the morning."],
      ["a mesa", "table", "🍽️", "noun", "f", "Põe os pratos na mesa.", "Put the plates on the table."],
      ["a cadeira", "chair", "🪑", "noun", "f", "Esta cadeira está partida.", "This chair is broken."],
      ["o sofá", "sofa", "🛋️", "noun", "m", "Leio no sofá à noite.", "I read on the sofa at night."],
    ],
  },
  {
    theme: "house", tier: "A2", rows: [
      ["o quarto", "bedroom", "🛌", "noun", "m", "O quarto dá para o jardim.", "The bedroom faces the garden."],
      ["a sala", "living room", "📺", "noun", "f", "Vemos televisão na sala.", "We watch TV in the living room."],
      ["o corredor", "hallway", "🚪", "noun", "m", "O corredor é estreito.", "The hallway is narrow."],
      ["a escada", "stairs", "🪜", "noun", "f", "Subo a escada.", "I go up the stairs."],
      ["o teto", "ceiling", "🏚️", "noun", "m", "O teto é muito alto.", "The ceiling is very high."],
      ["o chão", "floor", "🧱", "noun", "m", "O chão é de madeira.", "The floor is wooden."],
      ["o armário", "wardrobe", "🚪", "noun", "m", "Guardo a roupa no armário.", "I keep my clothes in the wardrobe."],
      ["o frigorífico", "fridge", "🧊", "noun", "m", "O leite está no frigorífico.", "The milk is in the fridge."],
      ["o candeeiro", "lamp", "💡", "noun", "m", "Acende o candeeiro.", "Turn on the lamp."],
      ["o jardim", "garden", "🌷", "noun", "m", "Tomamos café no jardim.", "We have coffee in the garden."],
    ],
  },

  {
    theme: "house", tier: "B1", rows: [
      ["a renda", "rent", "🔑", "noun", "f", "A renda sobe todos os anos.", "The rent goes up every year."],
      ["a hipoteca", "mortgage", "🏦", "noun", "f", "Pagamos a hipoteca a trinta anos.", "We pay the mortgage over thirty years."],
      ["o inquilino", "tenant", "🚪", "noun", "m", "O inquilino assina o contrato.", "The tenant signs the contract."],
      ["a mudança", "move", "📦", "noun", "f", "A mudança foi extenuante.", "The move was exhausting."],
      ["mobilado", "furnished", "🛋️", "adjective", "", "Alugo um apartamento mobilado.", "I rent a furnished flat."],
      ["o eletrodoméstico", "appliance", "🔌", "noun", "m", "Comprei um eletrodoméstico novo.", "I bought a new appliance."],
      ["a fachada", "façade", "🏛️", "noun", "f", "A fachada precisa de pintura.", "The façade needs paint."],
      ["a arrecadação", "storage room", "🗄️", "noun", "f", "Guardo as caixas na arrecadação.", "I keep the boxes in the storage room."],
      ["acolhedor", "cosy", "🕯️", "adjective", "", "A sala dela é muito acolhedora.", "Her living room is very cosy."],
      ["o aquecimento central", "heating", "🔥", "noun", "m", "O aquecimento central não funciona.", "The heating isn't working."],
    ],
  },

  // ---- Food & Drink ----------------------------------------------------
  {
    theme: "food", tier: "A1", rows: [
      ["a água", "water", "💧", "noun", "f", "Bebo muita água.", "I drink a lot of water."],
      ["o pão", "bread", "🍞", "noun", "m", "Compro pão fresco.", "I buy fresh bread."],
      ["o leite", "milk", "🥛", "noun", "m", "Tomo café com leite.", "I have coffee with milk."],
      ["o café", "coffee", "☕", "noun", "m", "O café está quente.", "The coffee is hot."],
      ["a fruta", "fruit", "🍎", "noun", "f", "A fruta é muito saudável.", "Fruit is very healthy."],
      ["a carne", "meat", "🥩", "noun", "f", "Não como carne vermelha.", "I don't eat red meat."],
      ["o pescado", "fish", "🐟", "noun", "m", "O pescado está fresco.", "The fish is fresh."],
      ["o ovo", "egg", "🥚", "noun", "m", "Ao pequeno-almoço como um ovo.", "I have an egg for breakfast."],
      ["o queijo", "cheese", "🧀", "noun", "m", "Gosto de queijo forte.", "I like strong cheese."],
      ["a comida", "food", "🍽️", "noun", "f", "A comida está pronta.", "The food is ready."],
    ],
  },
  {
    theme: "food", tier: "A2", rows: [
      ["o pequeno-almoço", "breakfast", "🥐", "noun", "m", "O pequeno-almoço é às oito.", "Breakfast is at eight."],
      ["o almoço", "lunch", "🍲", "noun", "m", "O almoço dura uma hora.", "Lunch lasts an hour."],
      ["o jantar", "dinner", "🍝", "noun", "m", "Preparo o jantar cedo.", "I make dinner early."],
      ["a receita", "recipe", "📋", "noun", "f", "Sigo a receita passo a passo.", "I follow the recipe step by step."],
      ["o açúcar", "sugar", "🍬", "noun", "m", "Não tomo açúcar no chá.", "I don't take sugar in my tea."],
      ["o sal", "salt", "🧂", "noun", "m", "Falta sal na sopa.", "The soup needs salt."],
      ["o azeite", "oil", "🫒", "noun", "m", "Cozinho com azeite.", "I cook with olive oil."],
      ["o legume", "vegetable", "🥦", "noun", "m", "Como legumes todos os dias.", "I eat vegetables every day."],
      ["a sobremesa", "dessert", "🍰", "noun", "f", "Queres sobremesa?", "Would you like dessert?"],
      ["a bebida", "drink", "🥤", "noun", "f", "Que bebida preferes?", "Which drink do you prefer?"],
    ],
  },

  {
    theme: "food", tier: "B1", rows: [
      ["o ingrediente", "ingredient", "🧄", "noun", "m", "Falta um ingrediente fundamental.", "A key ingredient is missing."],
      ["a colher de sopa", "spoonful", "🥄", "noun", "f", "Adiciona uma colher de sopa de mel.", "Add a spoonful of honey."],
      ["ferver", "to boil", "♨️", "verb", "", "Ferve a água dois minutos.", "Boil the water for two minutes."],
      ["fritar", "to fry", "🍳", "verb", "", "Frito as batatas em azeite.", "I fry the potatoes in oil."],
      ["o sabor", "flavour", "👅", "noun", "m", "Este prato tem muito sabor.", "This dish has a lot of flavour."],
      ["insosso", "bland", "😐", "adjective", "", "A sopa está insossa.", "The soup is bland."],
      ["picante", "spicy", "🌶️", "adjective", "", "Gosto de comida picante.", "I like spicy food."],
      ["o lanche", "afternoon snack", "🍪", "noun", "m", "As crianças comem o lanche.", "The children have their afternoon snack."],
      ["expirar", "to expire", "📆", "verb", "", "O leite expira amanhã.", "The milk expires tomorrow."],
      ["o congelador", "freezer", "❄️", "noun", "m", "Guarda o peixe no congelador.", "Keep the fish in the freezer."],
    ],
  },

  // ---- Body & Health ---------------------------------------------------
  {
    theme: "body-health", tier: "A1", rows: [
      ["o corpo", "body", "🧍", "noun", "m", "O corpo precisa de descanso.", "The body needs rest."],
      ["a cabeça", "head", "🗣️", "noun", "f", "Dói-me a cabeça.", "My head hurts."],
      ["a mão", "hand", "✋", "noun", "f", "Levanta a mão.", "Raise your hand."],
      ["o olho", "eye", "👁️", "noun", "m", "Tem um olho negro.", "He has a black eye."],
      ["a boca", "mouth", "👄", "noun", "f", "Abre a boca.", "Open your mouth."],
      ["o pé", "foot", "🦶", "noun", "m", "Dói-me o pé esquerdo.", "My left foot hurts."],
      ["o braço", "arm", "💪", "noun", "m", "Partiu o braço.", "He broke his arm."],
      ["a perna", "leg", "🦵", "noun", "f", "Estica a perna.", "Stretch your leg."],
      ["o cabelo", "hair", "💇", "noun", "m", "Tem o cabelo comprido.", "She has long hair."],
      ["o nariz", "nose", "👃", "noun", "m", "Tenho comichão no nariz.", "My nose is itchy."],
    ],
  },
  {
    theme: "body-health", tier: "A2", rows: [
      ["a saúde", "health", "❤️‍🩹", "noun", "f", "Cuida da tua saúde.", "Take care of your health."],
      ["a doença", "illness", "🤒", "noun", "f", "É uma doença comum.", "It is a common illness."],
      ["a dor", "pain", "😣", "noun", "f", "Sinto uma dor forte.", "I feel a strong pain."],
      ["a febre", "fever", "🌡️", "noun", "f", "A criança tem febre.", "The child has a fever."],
      ["o comprimido", "pill", "💊", "noun", "m", "Tomo um comprimido por dia.", "I take one pill a day."],
      ["o médico", "doctor", "🩺", "noun", "m", "Vou ao médico amanhã.", "I'm going to the doctor tomorrow."],
      ["o hospital", "hospital", "🏥", "noun", "m", "O hospital fica perto.", "The hospital is nearby."],
      ["a consulta médica", "medical appointment", "📅", "phrase", "", "Tenho uma consulta médica na sexta.", "I have a medical appointment on Friday."],
      ["tossir", "to cough", "😷", "verb", "", "Não paro de tossir.", "I can't stop coughing."],
      ["saudável", "healthy", "🥗", "adjective", "", "Leva uma vida saudável.", "She leads a healthy life."],
    ],
  },

  {
    theme: "body-health", tier: "B1", rows: [
      ["o sintoma", "symptom", "🤒", "noun", "m", "O sintoma principal é a tosse.", "The main symptom is a cough."],
      ["a receita médica", "prescription", "📝", "phrase", "", "Preciso de uma receita médica.", "I need a prescription."],
      ["o tratamento", "treatment", "💉", "noun", "m", "O tratamento dura um mês.", "The treatment lasts a month."],
      ["a ferida", "wound", "🩹", "noun", "f", "Limpa a ferida com cuidado.", "Clean the wound carefully."],
      ["recuperar", "to recover", "🌤️", "verb", "", "Recupera pouco a pouco.", "He's recovering little by little."],
      ["o batimento", "heartbeat", "💓", "noun", "m", "Sinto o batimento do coração.", "I feel the heartbeat."],
      ["tonto", "dizzy", "😵", "adjective", "", "Sinto-me tonto ao levantar-me.", "I feel dizzy when I get up."],
      ["a cirurgia", "surgery", "🏥", "noun", "f", "A cirurgia correu bem.", "The surgery went well."],
      ["o bem-estar", "wellbeing", "🧘", "noun", "m", "O desporto melhora o bem-estar.", "Sport improves wellbeing."],
      ["espirrar", "to sneeze", "🤧", "verb", "", "Não para de espirrar.", "He won't stop sneezing."],
    ],
  },

  // ---- Daily Routine ---------------------------------------------------
  {
    theme: "daily-routine", tier: "A1", rows: [
      ["acordar", "to wake up", "⏰", "verb", "", "Acordo às sete.", "I wake up at seven."],
      ["levantar-se", "to get up", "🛌", "verb", "", "Levanto-me logo a seguir.", "I get up right away."],
      ["tomar duche", "to shower", "🚿", "verb", "", "Tomo duche de manhã.", "I shower in the morning."],
      ["vestir-se", "to get dressed", "👕", "verb", "", "Visto-me depressa.", "I get dressed quickly."],
      ["tomar o pequeno-almoço", "to have breakfast", "🥣", "verb", "", "Tomo o pequeno-almoço em casa.", "I have breakfast at home."],
      ["a rotina", "routine", "🔁", "noun", "f", "Tenho uma rotina fixa.", "I have a fixed routine."],
      ["o horário", "schedule", "🗓️", "noun", "m", "O meu horário muda todas as semanas.", "My schedule changes every week."],
      ["cedo", "early", "🌅", "adverb", "", "Deito-me cedo.", "I go to bed early."],
      ["tarde", "late", "🌙", "adverb", "", "Chego tarde ao trabalho.", "I arrive late to work."],
      ["todos os dias", "every day", "📆", "phrase", "", "Estudo português todos os dias.", "I study Portuguese every day."],
    ],
  },
  {
    theme: "daily-routine", tier: "A2", rows: [
      ["deitar-se", "to go to bed", "🛏️", "verb", "", "Deito-me à meia-noite.", "I go to bed at midnight."],
      ["pentear-se", "to comb one's hair", "💇", "verb", "", "Penteio-me antes de sair.", "I comb my hair before leaving."],
      ["escovar", "to brush", "🪥", "verb", "", "Escovo os dentes.", "I brush my teeth."],
      ["lavar-se", "to wash oneself", "🧼", "verb", "", "Lavo as mãos.", "I wash my hands."],
      ["descansar", "to rest", "🛋️", "verb", "", "Descanso um bocado depois de comer.", "I rest a while after eating."],
      ["as tarefas domésticas", "chores", "🧹", "noun", "f", "Faço as tarefas domésticas ao sábado.", "I do the chores on Saturdays."],
      ["costumar", "to usually do", "🔁", "verb", "", "Costumo correr à tarde.", "I usually run in the afternoon."],
      ["ocupado", "busy", "📚", "adjective", "", "Hoje estou muito ocupado.", "Today I am very busy."],
      ["livre", "free", "🆓", "adjective", "", "Tenho a tarde livre.", "I have the afternoon free."],
      ["enquanto", "while", "⏳", "conjunction", "", "Cozinho enquanto ouço música.", "I cook while I listen to music."],
    ],
  },

  {
    theme: "daily-routine", tier: "B1", rows: [
      ["madrugar", "to get up early", "🌄", "verb", "", "Madrugo para ir ao ginásio.", "I get up early to go to the gym."],
      ["o madrugar", "early rise", "⏰", "noun", "m", "O madrugar de hoje deixou-me cansado.", "Today's early start left me tired."],
      ["fazer serão", "to stay up late", "🌃", "verb", "", "Não gosto de fazer serão.", "I don't like staying up late."],
      ["a sesta", "nap", "😴", "noun", "f", "Faço uma sesta curta.", "I take a short nap."],
      ["o afazer", "task", "🧺", "noun", "m", "Tenho muitos afazeres hoje.", "I have many tasks today."],
      ["aproveitar", "to make the most of", "☀️", "verb", "", "Aproveito a manhã para estudar.", "I make the most of the morning to study."],
      ["a azáfama", "hustle and bustle", "🏃", "noun", "f", "A azáfama diária cansa-me.", "The daily hustle tires me out."],
      ["relaxar", "to relax", "🧖", "verb", "", "Relaxo com um banho quente.", "I relax with a hot bath."],
      ["quotidiano", "everyday", "📅", "adjective", "", "Faz parte da minha vida quotidiana.", "It's part of my everyday life."],
      ["pontual", "punctual", "⏱️", "adjective", "", "É sempre muito pontual.", "He's always very punctual."],
    ],
  },

  // ---- Work & Study ----------------------------------------------------
  {
    theme: "work-study", tier: "A1", rows: [
      ["o trabalho", "work", "💼", "noun", "m", "Vou para o trabalho de comboio.", "I go to work by train."],
      ["a escola", "school", "🏫", "noun", "f", "As crianças vão à escola.", "The children go to school."],
      ["o professor", "teacher", "👨‍🏫", "noun", "m", "O professor explica bem.", "The teacher explains well."],
      ["o estudante", "student", "🎓", "noun", "m", "Sou estudante de medicina.", "I am a medical student."],
      ["o escritório", "office", "🏢", "noun", "m", "O escritório está fechado.", "The office is closed."],
      ["o livro", "book", "📖", "noun", "m", "Leio um livro interessante.", "I'm reading an interesting book."],
      ["o computador", "computer", "💻", "noun", "m", "Trabalho com o computador.", "I work with the computer."],
      ["estudar", "to study", "📚", "verb", "", "Estudo à noite.", "I study at night."],
      ["aprender", "to learn", "🧠", "verb", "", "Quero aprender português.", "I want to learn Portuguese."],
      ["o exame", "exam", "📝", "noun", "m", "O exame é difícil.", "The exam is difficult."],
    ],
  },
  {
    theme: "work-study", tier: "A2", rows: [
      ["a empresa", "company", "🏢", "noun", "f", "Trabalho numa empresa grande.", "I work at a big company."],
      ["o chefe", "boss", "🧑‍💼", "noun", "m", "O meu chefe é muito justo.", "My boss is very fair."],
      ["o ordenado", "salary", "💶", "noun", "m", "O ordenado é bom.", "The salary is good."],
      ["a reunião", "meeting", "📊", "noun", "f", "A reunião é às dez.", "The meeting is at ten."],
      ["o horário de trabalho", "working hours", "🕘", "phrase", "", "O meu horário de trabalho é flexível.", "My working hours are flexible."],
      ["o curso", "degree", "🎓", "noun", "m", "Tirei um curso técnico.", "I studied a technical degree."],
      ["o projeto", "project", "📁", "noun", "m", "O projeto corre bem.", "The project is going well."],
      ["contratar", "to hire", "🤝", "verb", "", "Vão contratar duas pessoas.", "They are going to hire two people."],
      ["o colega", "colleague", "👥", "noun", "m", "O meu colega é do Peru.", "My colleague is from Peru."],
      ["a entrevista", "interview", "🗣️", "noun", "f", "Tenho uma entrevista na segunda.", "I have an interview on Monday."],
    ],
  },

  {
    theme: "work-study", tier: "B1", rows: [
      ["a promoção", "promotion", "📈", "noun", "f", "Deram-lhe uma promoção.", "They gave him a promotion."],
      ["a jornada", "workday", "🕘", "noun", "f", "Tenho uma jornada longa.", "I have a long workday."],
      ["o prazo", "deadline", "⏳", "noun", "m", "O prazo termina na sexta.", "The deadline ends on Friday."],
      ["a bolsa", "scholarship", "🎓", "noun", "f", "Consegui uma bolsa.", "I got a scholarship."],
      ["desempenhar", "to carry out", "🛠️", "verb", "", "Desempenha um papel importante.", "He plays an important role."],
      ["o currículo", "CV", "📄", "noun", "m", "Atualizo o meu currículo.", "I'm updating my CV."],
      ["a formação", "training", "📚", "noun", "f", "A empresa oferece formação.", "The company offers training."],
      ["o despedimento", "dismissal", "📉", "noun", "m", "O despedimento foi uma surpresa.", "The dismissal was a surprise."],
      ["empreender", "to start a venture", "🚀", "verb", "", "Quer empreender um negócio.", "She wants to start a business."],
      ["o quadro de pessoal", "staff", "👥", "noun", "m", "O quadro de pessoal cresceu este ano.", "The staff grew this year."],
    ],
  },

  // ---- Travel ----------------------------------------------------------
  {
    theme: "travel", tier: "A1", rows: [
      ["a viagem", "trip", "🧳", "noun", "f", "A viagem foi longa.", "The trip was long."],
      ["o avião", "plane", "✈️", "noun", "m", "O avião parte às seis.", "The plane leaves at six."],
      ["o comboio", "train", "🚆", "noun", "m", "Apanho o comboio todas as manhãs.", "I take the train every morning."],
      ["o carro", "car", "🚗", "noun", "m", "Vamos de carro.", "We're going by car."],
      ["o hotel", "hotel", "🏨", "noun", "m", "O hotel fica na praia.", "The hotel is on the beach."],
      ["o bilhete", "ticket", "🎫", "noun", "m", "Compro o bilhete pela internet.", "I buy the ticket online."],
      ["a mala", "suitcase", "🧳", "noun", "f", "Faço a mala esta noite.", "I'll pack the suitcase tonight."],
      ["o aeroporto", "airport", "🛫", "noun", "m", "Chego cedo ao aeroporto.", "I arrive at the airport early."],
      ["viajar", "to travel", "🌍", "verb", "", "Adoro viajar sozinho.", "I love travelling alone."],
      ["o mapa", "map", "🗺️", "noun", "m", "Olho para o mapa da cidade.", "I look at the city map."],
    ],
  },
  {
    theme: "travel", tier: "A2", rows: [
      ["o passaporte", "passport", "🛂", "noun", "m", "O meu passaporte expira em breve.", "My passport expires soon."],
      ["a fronteira", "border", "🚧", "noun", "f", "Atravessamos a fronteira de noite.", "We cross the border at night."],
      ["a estação", "station", "🚉", "noun", "f", "A estação fica longe.", "The station is far away."],
      ["a bagagem", "luggage", "🧳", "noun", "f", "Despacho a bagagem.", "I check in the luggage."],
      ["o destino", "destination", "📍", "noun", "m", "O nosso destino é Sevilha.", "Our destination is Seville."],
      ["reservar", "to book", "📅", "verb", "", "Reservo um quarto duplo.", "I book a double room."],
      ["o voo", "flight", "🛬", "noun", "m", "O voo está atrasado.", "The flight is delayed."],
      ["a chegada", "arrival", "🛬", "noun", "f", "A chegada é às nove.", "Arrival is at nine."],
      ["a saída", "departure", "🚪", "noun", "f", "A saída fica à direita.", "The exit is on the right."],
      ["o turista", "tourist", "📸", "noun", "m", "O turista tira fotografias.", "The tourist takes photos."],
    ],
  },

  {
    theme: "travel", tier: "B1", rows: [
      ["o alojamento", "accommodation", "🏨", "noun", "m", "O alojamento inclui o pequeno-almoço.", "The accommodation includes breakfast."],
      ["a escala", "stopover", "🛑", "noun", "f", "O voo tem uma escala.", "The flight has a stopover."],
      ["o itinerário", "itinerary", "🗺️", "noun", "m", "Planeio o itinerário com cuidado.", "I plan the itinerary carefully."],
      ["o seguro de viagem", "travel insurance", "🛡️", "phrase", "", "Faço um seguro de viagem.", "I take out travel insurance."],
      ["embarcar", "to board", "🛫", "verb", "", "Embarcamos dentro de meia hora.", "We board in half an hour."],
      ["a alfândega", "customs", "🛂", "noun", "f", "Passamos pela alfândega.", "We go through customs."],
      ["o trajeto", "journey", "🚌", "noun", "m", "O trajeto dura três horas.", "The journey takes three hours."],
      ["alojar-se", "to stay", "🛏️", "verb", "", "Alojámo-nos numa pensão.", "We stay in a guesthouse."],
      ["a bússola", "compass", "🧭", "noun", "f", "Levo uma bússola na mochila.", "I carry a compass in my backpack."],
      ["a mochila", "backpack", "🎒", "noun", "f", "Preparo a mochila para a viagem.", "I pack the backpack for the trip."],
    ],
  },

  // ---- Time & Weather --------------------------------------------------
  {
    theme: "time-weather", tier: "A1", rows: [
      ["o tempo", "weather", "🌤️", "noun", "m", "Hoje está bom tempo.", "The weather is nice today."],
      ["o sol", "sun", "☀️", "noun", "m", "Está sol esta manhã.", "It's sunny this morning."],
      ["a chuva", "rain", "🌧️", "noun", "f", "A chuva não para.", "The rain won't stop."],
      ["o vento", "wind", "💨", "noun", "m", "Está muito vento.", "It's very windy."],
      ["a neve", "snow", "❄️", "noun", "f", "A neve cobre o campo.", "The snow covers the field."],
      ["o dia", "day", "📆", "noun", "m", "Está um dia lindo.", "It's a lovely day."],
      ["a noite", "night", "🌙", "noun", "f", "A noite está fria.", "The night is cold."],
      ["hoje", "today", "📅", "adverb", "", "Hoje não trabalho.", "I'm not working today."],
      ["amanhã", "tomorrow", "🌅", "adverb", "", "Amanhã vamos ao cinema.", "Tomorrow we're going to the cinema."],
      ["está calor", "it's hot", "🥵", "phrase", "", "Em agosto está calor.", "In August it's hot."],
    ],
  },
  {
    theme: "time-weather", tier: "A2", rows: [
      ["a temperatura", "temperature", "🌡️", "noun", "f", "A temperatura baixa à noite.", "The temperature drops at night."],
      ["a nuvem", "cloud", "☁️", "noun", "f", "Há uma nuvem negra.", "There's a black cloud."],
      ["a tempestade", "storm", "⛈️", "noun", "f", "Vem aí uma tempestade forte.", "A strong storm is coming."],
      ["o grau", "degree", "🔆", "noun", "m", "Estamos a trinta graus.", "It's thirty degrees."],
      ["o prognóstico", "forecast", "📰", "noun", "m", "O prognóstico anuncia chuva.", "The forecast predicts rain."],
      ["desanuviado", "clear", "🌞", "adjective", "", "O céu está desanuviado.", "The sky is clear."],
      ["húmido", "humid", "💦", "adjective", "", "O clima é húmido aqui.", "The climate is humid here."],
      ["a estação do ano", "season", "🍂", "phrase", "", "O outono é a minha estação do ano preferida.", "Autumn is my favourite season."],
      ["o amanhecer", "dawn", "🌄", "noun", "m", "Saímos ao amanhecer.", "We leave at dawn."],
      ["está frio", "it's cold", "🥶", "phrase", "", "No inverno está frio.", "In winter it's cold."],
    ],
  },

  {
    theme: "time-weather", tier: "B1", rows: [
      ["o chuvisco", "light shower / drizzle", "🌦️", "noun", "m", "Caem chuviscos à tarde.", "Light showers fall in the afternoon."],
      ["o nevoeiro", "fog", "🌫️", "noun", "m", "Há nevoeiro na estrada.", "There's fog on the road."],
      ["o granizo", "hail", "🌨️", "noun", "m", "O granizo danificou os carros.", "The hail damaged the cars."],
      ["soalheiro", "sunny", "🌞", "adjective", "", "Amanhã será um dia soalheiro.", "Tomorrow will be a sunny day."],
      ["nublado", "cloudy", "☁️", "adjective", "", "O céu está nublado.", "The sky is cloudy."],
      ["a seca", "drought", "🏜️", "noun", "f", "A seca afeta o campo.", "The drought affects the countryside."],
      ["o relâmpago", "lightning", "⚡", "noun", "m", "Vimos um relâmpago ao longe.", "We saw distant lightning."],
      ["gelar", "to freeze", "🧊", "verb", "", "Ontem à noite gelou no vale.", "Last night it froze in the valley."],
      ["o entardecer", "dusk", "🌇", "noun", "m", "Passeamos ao entardecer.", "We walk at dusk."],
      ["ameno", "mild", "🌡️", "adjective", "", "O clima é ameno no outono.", "The climate is mild in autumn."],
    ],
  },

  // ---- Shopping --------------------------------------------------------
  {
    theme: "shopping", tier: "A1", rows: [
      ["a loja", "shop", "🏪", "noun", "f", "A loja abre às nove.", "The shop opens at nine."],
      ["o dinheiro", "money", "💶", "noun", "m", "Não levo dinheiro trocado.", "I don't have any loose change."],
      ["o preço", "price", "🏷️", "noun", "m", "O preço é muito alto.", "The price is very high."],
      ["comprar", "to buy", "🛒", "verb", "", "Quero comprar sapatos.", "I want to buy shoes."],
      ["pagar", "to pay", "💳", "verb", "", "Pago com cartão.", "I pay by card."],
      ["caro", "expensive", "💸", "adjective", "", "Este relógio é muito caro.", "This watch is very expensive."],
      ["barato", "cheap", "🪙", "adjective", "", "A fruta está barata hoje.", "Fruit is cheap today."],
      ["o euro", "euro", "💶", "noun", "m", "Custa cinco euros.", "It costs five euros."],
      ["a roupa", "clothes", "👕", "noun", "f", "Compro roupa de inverno.", "I buy winter clothes."],
      ["o mercado", "market", "🧺", "noun", "m", "Vou ao mercado aos sábados.", "I go to the market on Saturdays."],
    ],
  },
  {
    theme: "shopping", tier: "A2", rows: [
      ["a oferta", "offer", "🏷️", "noun", "f", "Há uma oferta esta semana.", "There's an offer this week."],
      ["o desconto", "discount", "🔖", "noun", "m", "Fazem-me um desconto.", "They give me a discount."],
      ["o tamanho", "size", "📏", "noun", "m", "Que tamanho usas?", "What size do you wear?"],
      ["o recibo", "receipt", "🧾", "noun", "m", "Guarda o recibo.", "Keep the receipt."],
      ["a caixa", "checkout", "💰", "noun", "f", "Pague na caixa, por favor.", "Pay at the checkout, please."],
      ["gastar", "to spend", "💸", "verb", "", "Gasto pouco em roupa.", "I spend little on clothes."],
      ["devolver", "to return", "↩️", "verb", "", "Quero devolver esta camisa.", "I want to return this shirt."],
      ["o cliente", "customer", "🧑", "noun", "m", "O cliente pergunta sempre.", "The customer always asks."],
      ["experimentar", "to try on", "👗", "verb", "", "Experimento o vestido.", "I try on the dress."],
      ["a montra", "shop window", "🪟", "noun", "f", "Olho para a montra.", "I look at the shop window."],
    ],
  },

  {
    theme: "shopping", tier: "B1", rows: [
      ["o orçamento", "budget", "💰", "noun", "m", "Tenho um orçamento apertado.", "I have a tight budget."],
      ["a fatura", "invoice", "🧾", "noun", "f", "Peço a fatura, por favor.", "I ask for the invoice, please."],
      ["o reembolso", "refund", "💵", "noun", "m", "Solicito um reembolso.", "I request a refund."],
      ["regatear", "to haggle", "🤝", "verb", "", "É normal regatear no mercado.", "It's normal to haggle in the market."],
      ["esgotado", "sold out", "🚫", "adjective", "", "O produto está esgotado.", "The product is sold out."],
      ["a garantia", "warranty", "📑", "noun", "f", "A garantia dura dois anos.", "The warranty lasts two years."],
      ["o comércio", "business", "🏬", "noun", "m", "O comércio local resiste.", "Local business holds on."],
      ["acessível", "affordable", "🪙", "adjective", "", "É um preço acessível.", "It's an affordable price."],
      ["o envio", "delivery", "📦", "noun", "m", "O envio é gratuito.", "Delivery is free."],
      ["a marca", "brand", "🏷️", "noun", "f", "Prefiro esta marca.", "I prefer this brand."],
    ],
  },

  // ---- Town & Directions -----------------------------------------------
  {
    theme: "town", tier: "A1", rows: [
      ["a cidade", "city", "🏙️", "noun", "f", "Vivo numa cidade grande.", "I live in a big city."],
      ["a aldeia", "town", "🏘️", "noun", "f", "A minha aldeia é muito tranquila.", "My town is very quiet."],
      ["a rua", "street", "🛣️", "noun", "f", "A rua está vazia.", "The street is empty."],
      ["a praça", "square", "⛲", "noun", "f", "Encontramo-nos na praça.", "Let's meet in the square."],
      ["o parque", "park", "🌳", "noun", "m", "Passeio pelo parque.", "I walk through the park."],
      ["o banco", "bank", "🏦", "noun", "m", "O banco fecha às duas.", "The bank closes at two."],
      ["o restaurante", "restaurant", "🍴", "noun", "m", "O restaurante é novo.", "The restaurant is new."],
      ["a igreja", "church", "⛪", "noun", "f", "A igreja é do século XV.", "The church is from the 15th century."],
      ["o museu", "museum", "🏛️", "noun", "m", "O museu abre ao domingo.", "The museum opens on Sunday."],
      ["o supermercado", "supermarket", "🛒", "noun", "m", "Vou ao supermercado a pé.", "I walk to the supermarket."],
    ],
  },
  {
    theme: "town", tier: "A2", rows: [
      ["a esquina", "corner", "📐", "noun", "f", "Vira na esquina.", "Turn at the corner."],
      ["o semáforo", "traffic light", "🚦", "noun", "m", "Para no semáforo.", "Stop at the traffic light."],
      ["a ponte", "bridge", "🌉", "noun", "f", "Atravessa a ponte.", "Cross the bridge."],
      ["o passeio", "pavement", "🚶", "noun", "m", "Anda no passeio.", "Walk on the pavement."],
      ["a câmara municipal", "town hall", "🏛️", "noun", "f", "A câmara municipal fica no centro.", "The town hall is in the centre."],
      ["virar", "to turn", "↩️", "verb", "", "Vira à esquerda.", "Turn left."],
      ["seguir em frente", "to go straight", "⬆️", "phrase", "", "Segue em frente duas ruas.", "Go straight on for two streets."],
      ["a paragem", "stop", "🚏", "noun", "f", "A paragem é aqui mesmo.", "The stop is right here."],
      ["o bairro", "neighbourhood", "🏘️", "noun", "m", "O meu bairro é muito seguro.", "My neighbourhood is very safe."],
      ["atravessar", "to cross", "🚸", "verb", "", "Atravessa com cuidado.", "Cross carefully."],
    ],
  },

  {
    theme: "town", tier: "B1", rows: [
      ["o centro histórico", "old town", "🏰", "phrase", "", "O centro histórico é lindo.", "The old town is beautiful."],
      ["os arredores", "outskirts", "🌆", "noun", "m", "Vivem nos arredores.", "They live on the outskirts."],
      ["o peão", "pedestrian", "🚶", "noun", "m", "O peão tem prioridade.", "The pedestrian has priority."],
      ["a rotunda", "roundabout", "🔄", "noun", "f", "Sai na segunda saída da rotunda.", "Take the second exit at the roundabout."],
      ["a iluminação pública", "street lighting", "💡", "noun", "f", "A iluminação pública é nova.", "The street lighting is new."],
      ["concorrido", "crowded", "👥", "adjective", "", "É uma zona muito concorrida.", "It's a very crowded area."],
      ["o quiosque", "kiosk", "🗞️", "noun", "m", "Compro o jornal no quiosque.", "I buy the newspaper at the kiosk."],
      ["a urbanização", "housing estate", "🏘️", "noun", "f", "Vivo numa urbanização tranquila.", "I live in a quiet housing estate."],
      ["pedonal", "pedestrian", "🚸", "adjective", "", "É uma rua pedonal.", "It's a pedestrian street."],
      ["a esquadra", "police station", "🚓", "noun", "f", "A esquadra fica na praça.", "The police station is on the square."],
    ],
  },

  // ---- Leisure ---------------------------------------------------------
  {
    theme: "leisure", tier: "A1", rows: [
      ["a música", "music", "🎵", "noun", "f", "Ouço música clássica.", "I listen to classical music."],
      ["o cinema", "cinema", "🎬", "noun", "m", "Vamos ao cinema na sexta.", "We're going to the cinema on Friday."],
      ["o desporto", "sport", "🏃", "noun", "m", "Faço desporto três vezes por semana.", "I do sport three times a week."],
      ["a festa", "party", "🎉", "noun", "f", "A festa é em minha casa.", "The party is at my house."],
      ["o futebol", "football", "⚽", "noun", "m", "O futebol é muito popular.", "Football is very popular."],
      ["dançar", "to dance", "💃", "verb", "", "Gosto de dançar salsa.", "I like to dance salsa."],
      ["cantar", "to sing", "🎤", "verb", "", "Canta muito bem.", "She sings very well."],
      ["ler", "to read", "📖", "verb", "", "Leio antes de dormir.", "I read before sleeping."],
      ["jogar", "to play", "🎮", "verb", "", "Jogo com os meus amigos.", "I play with my friends."],
      ["nadar", "to swim", "🏊", "verb", "", "Nado no mar no verão.", "I swim in the sea in summer."],
    ],
  },
  {
    theme: "leisure", tier: "A2", rows: [
      ["o concerto", "concert", "🎶", "noun", "m", "O concerto começa tarde.", "The concert starts late."],
      ["a entrada", "ticket", "🎟️", "noun", "f", "A entrada custa vinte euros.", "The ticket costs twenty euros."],
      ["a equipa", "team", "👕", "noun", "f", "A minha equipa ganhou o jogo.", "My team won the match."],
      ["o adepto", "fan", "📣", "noun", "m", "É um adepto do basquetebol.", "He is a basketball fan."],
      ["o hobby", "hobby", "🎨", "noun", "m", "O meu hobby é a fotografia.", "My hobby is photography."],
      ["o espetáculo", "show", "🎭", "noun", "m", "O espetáculo foi incrível.", "The show was amazing."],
      ["combinar", "to meet up", "🤝", "verb", "", "Combinamos às sete.", "Let's meet up at seven."],
      ["desfrutar", "to enjoy", "😄", "verb", "", "Desfruto dos fins de semana.", "I enjoy the weekends."],
      ["o lazer", "leisure", "🛋️", "noun", "m", "Dedico pouco tempo ao lazer.", "I devote little time to leisure."],
      ["divertir-se", "to have a good time", "🥳", "phrase", "", "Divertimo-nos muito ontem à noite.", "We had a great time last night."],
    ],
  },

  {
    theme: "leisure", tier: "B1", rows: [
      ["o passatempo", "pastime", "🎲", "noun", "m", "Colecionar selos é o seu passatempo.", "Collecting stamps is his pastime."],
      ["a estreia", "premiere", "🎬", "noun", "f", "Vamos à estreia esta noite.", "We're going to the premiere tonight."],
      ["ensaiar", "to rehearse", "🎻", "verb", "", "Ensaiamos às terças.", "We rehearse on Tuesdays."],
      ["a bilheteira", "box office", "🎟️", "noun", "f", "Compro as entradas na bilheteira.", "I buy the tickets at the box office."],
      ["apostar", "to bet", "🎰", "verb", "", "Não gosto de apostar dinheiro.", "I don't like betting money."],
      ["o xadrez", "chess", "♟️", "noun", "m", "Jogo xadrez com o meu avô.", "I play chess with my grandfather."],
      ["a caminhada", "hiking", "🥾", "noun", "f", "A caminhada relaxa-me.", "Hiking relaxes me."],
      ["colecionar", "to collect", "🗂️", "verb", "", "Coleciona moedas antigas.", "He collects old coins."],
      ["o espetador", "spectator", "👀", "noun", "m", "Os espetadores aplaudiram.", "The spectators applauded."],
      ["divertido", "entertaining", "😄", "adjective", "", "O filme é muito divertido.", "The film is very entertaining."],
    ],
  },

  // ---- Feelings --------------------------------------------------------
  {
    theme: "feelings", tier: "A1", rows: [
      ["feliz", "happy", "😊", "adjective", "", "Hoje estou muito feliz.", "I'm very happy today."],
      ["triste", "sad", "😢", "adjective", "", "Sente-se triste.", "He feels sad."],
      ["cansado", "tired", "😫", "adjective", "", "Estou cansado da viagem.", "I'm tired from the trip."],
      ["contente", "glad", "🙂", "adjective", "", "Estamos contentes com a casa.", "We're glad with the house."],
      ["zangado", "angry", "😠", "adjective", "", "Está zangado comigo.", "He's angry with me."],
      ["nervoso", "nervous", "😬", "adjective", "", "Estou nervoso por causa do exame.", "I'm nervous about the exam."],
      ["o medo", "fear", "😨", "noun", "m", "Tenho medo do escuro.", "I'm afraid of the dark."],
      ["a alegria", "joy", "😁", "noun", "f", "Senti uma grande alegria.", "I felt great joy."],
      ["aborrecido", "bored", "🥱", "adjective", "", "Estou aborrecido em casa.", "I'm bored at home."],
      ["ter fome", "to be hungry", "🍽️", "phrase", "", "Tenho muita fome.", "I'm very hungry."],
    ],
  },
  {
    theme: "feelings", tier: "A2", rows: [
      ["emocionado", "excited", "🤩", "adjective", "", "Estou emocionado com a viagem.", "I'm excited about the trip."],
      ["preocupado", "worried", "😟", "adjective", "", "Está preocupada com o filho.", "She's worried about her son."],
      ["surpreendido", "surprised", "😲", "adjective", "", "Fiquei surpreendido.", "I was surprised."],
      ["orgulhoso", "proud", "😌", "adjective", "", "Estou orgulhoso de ti.", "I'm proud of you."],
      ["envergonhado", "embarrassed", "😳", "adjective", "", "Sentiu-se envergonhado.", "He felt embarrassed."],
      ["os ciúmes", "jealousy", "😒", "noun", "m", "Os ciúmes não ajudam.", "Jealousy doesn't help."],
      ["o ânimo", "mood", "💪", "noun", "m", "Hoje tenho bom ânimo.", "Today I'm in good spirits."],
      ["sentir-se", "to feel", "🫀", "verb", "", "Sinto-me muito melhor.", "I feel much better."],
      ["ter saudades", "to miss", "💔", "phrase", "", "Tenho saudades da minha família.", "I miss my family."],
      ["estar bem-disposto", "to be in a good mood", "😄", "phrase", "", "Hoje estou bem-disposto.", "Today I'm in a good mood."],
    ],
  },

  {
    theme: "feelings", tier: "B1", rows: [
      ["a angústia", "anguish", "😰", "noun", "f", "Senti uma angústia profunda.", "I felt deep anguish."],
      ["o alívio", "relief", "😮‍💨", "noun", "m", "Que alívio terminar o exame.", "What a relief to finish the exam."],
      ["assustado", "scared", "😱", "adjective", "", "A criança está assustada.", "The child is scared."],
      ["a confiança", "trust", "🤝", "noun", "f", "Tenho plena confiança em ti.", "I have full trust in you."],
      ["animar-se", "to cheer up", "🙂", "verb", "", "Anima-te, vai correr tudo bem.", "Cheer up, everything will be fine."],
      ["a deceção", "disappointment", "💧", "noun", "f", "Foi uma grande deceção.", "It was a big disappointment."],
      ["sobrecarregado", "overwhelmed", "😩", "adjective", "", "Estou sobrecarregado com o trabalho.", "I'm overwhelmed with work."],
      ["o carinho", "affection", "🥰", "noun", "m", "Tenho-lhe muito carinho.", "I'm very fond of him."],
      ["zangar-se", "to get angry", "😤", "verb", "", "Zanga-se por tudo.", "He gets angry about everything."],
      ["esperançoso", "hopeful", "🌈", "adjective", "", "Estou esperançoso quanto ao futuro.", "I'm hopeful about the future."],
    ],
  },

  // ---- Nature ----------------------------------------------------------
  {
    theme: "nature", tier: "A1", rows: [
      ["a árvore", "tree", "🌳", "noun", "f", "A árvore dá muita sombra.", "The tree gives a lot of shade."],
      ["a flor", "flower", "🌸", "noun", "f", "A flor cheira bem.", "The flower smells nice."],
      ["o mar", "sea", "🌊", "noun", "m", "O mar está calmo.", "The sea is calm."],
      ["a montanha", "mountain", "⛰️", "noun", "f", "Subimos a montanha.", "We climb the mountain."],
      ["o rio", "river", "🏞️", "noun", "m", "O rio passa pela aldeia.", "The river runs through the town."],
      ["o animal", "animal", "🐾", "noun", "m", "É um animal selvagem.", "It's a wild animal."],
      ["o cão", "dog", "🐕", "noun", "m", "O cão brinca no jardim.", "The dog plays in the garden."],
      ["o gato", "cat", "🐈", "noun", "m", "O gato dorme o dia todo.", "The cat sleeps all day."],
      ["o céu", "sky", "🌌", "noun", "m", "O céu está azul.", "The sky is blue."],
      ["a praia", "beach", "🏖️", "noun", "f", "Vamos à praia.", "We're going to the beach."],
    ],
  },
  {
    theme: "nature", tier: "A2", rows: [
      ["a floresta", "forest", "🌲", "noun", "f", "A floresta é muito densa.", "The forest is very dense."],
      ["a folha", "leaf", "🍃", "noun", "f", "Cai uma folha da árvore.", "A leaf falls from the tree."],
      ["a planta", "plant", "🪴", "noun", "f", "Rego a planta todos os dias.", "I water the plant every day."],
      ["o campo", "countryside", "🌾", "noun", "m", "Passamos o dia no campo.", "We spend the day in the countryside."],
      ["a ilha", "island", "🏝️", "noun", "f", "A ilha é muito pequena.", "The island is very small."],
      ["o pássaro", "bird", "🐦", "noun", "m", "O pássaro canta ao amanhecer.", "The bird sings at dawn."],
      ["o meio ambiente", "environment", "🌍", "phrase", "", "Há que cuidar do meio ambiente.", "We must take care of the environment."],
      ["poluir", "to pollute", "🏭", "verb", "", "Os carros poluem muito.", "Cars pollute a lot."],
      ["a paisagem", "landscape", "🖼️", "noun", "f", "A paisagem é impressionante.", "The landscape is stunning."],
      ["selvagem", "wild", "🐺", "adjective", "", "É uma zona selvagem.", "It's a wild area."],
    ],
  },

  {
    theme: "nature", tier: "B1", rows: [
      ["o meio rural", "rural environment", "🚜", "phrase", "", "Cresci no meio rural.", "I grew up in the countryside."],
      ["a cordilheira", "mountain range", "🏔️", "noun", "f", "A cordilheira separa os países.", "The mountain range separates the countries."],
      ["o ribeiro", "stream", "🏞️", "noun", "m", "O ribeiro desce da montanha.", "The stream comes down from the mountain."],
      ["a fauna", "wildlife", "🦌", "noun", "f", "A fauna local é variada.", "The local wildlife is varied."],
      ["a flora", "flora", "🌿", "noun", "f", "A flora da floresta é densa.", "The forest flora is dense."],
      ["semear", "to sow", "🌱", "verb", "", "Semeamos trigo na primavera.", "We sow wheat in spring."],
      ["a plantação", "crop", "🌾", "noun", "f", "A plantação precisa de água.", "The crop needs water."],
      ["o cume", "summit", "🗻", "noun", "m", "Chegámos ao cume ao meio-dia.", "We reach the summit at midday."],
      ["proteger", "to protect", "🛡️", "verb", "", "Devemos proteger a natureza.", "We must protect nature."],
      ["a espécie", "species", "🐾", "noun", "f", "É uma espécie em perigo.", "It's an endangered species."],
    ],
  },

  // ---- Describing Things -----------------------------------------------
  {
    theme: "describing", tier: "A1", rows: [
      ["grande", "big", "🐘", "adjective", "", "É uma casa grande.", "It's a big house."],
      ["pequeno", "small", "🐁", "adjective", "", "Tenho um carro pequeno.", "I have a small car."],
      ["novo", "new", "✨", "adjective", "", "É o meu telefone novo.", "It's my new phone."],
      ["velho", "old", "📦", "adjective", "", "É um edifício velho.", "It's an old building."],
      ["bom", "good", "👍", "adjective", "", "É um bom livro.", "It's a good book."],
      ["mau", "bad", "👎", "adjective", "", "Está mau tempo.", "The weather is bad."],
      ["bonito", "pretty", "🌷", "adjective", "", "Que vestido tão bonito.", "What a pretty dress."],
      ["feio", "ugly", "🙈", "adjective", "", "O quadro é um pouco feio.", "The painting is a bit ugly."],
      ["comprido", "long", "📏", "adjective", "", "É um caminho comprido.", "It's a long road."],
      ["curto", "short", "✂️", "adjective", "", "O vídeo é curto.", "The video is short."],
    ],
  },
  {
    theme: "describing", tier: "A2", rows: [
      ["amável", "kind", "🤗", "adjective", "", "O empregado de mesa é muito amável.", "The waiter is very kind."],
      ["inteligente", "intelligent", "🧠", "adjective", "", "É uma menina inteligente.", "She's an intelligent girl."],
      ["forte", "strong", "💪", "adjective", "", "O café está muito forte.", "The coffee is very strong."],
      ["fraco", "weak", "🥀", "adjective", "", "O sinal é fraco.", "The signal is weak."],
      ["rápido", "fast", "⚡", "adjective", "", "É um comboio rápido.", "It's a fast train."],
      ["lento", "slow", "🐢", "adjective", "", "O serviço é muito lento.", "The service is very slow."],
      ["limpo", "clean", "🧼", "adjective", "", "O quarto está limpo.", "The room is clean."],
      ["sujo", "dirty", "🧽", "adjective", "", "O chão está sujo.", "The floor is dirty."],
      ["difícil", "difficult", "🧗", "adjective", "", "O exercício é difícil.", "The exercise is difficult."],
      ["fácil", "easy", "✅", "adjective", "", "A pergunta é fácil.", "The question is easy."],
    ],
  },
  {
    theme: "describing", tier: "B1", rows: [
      ["estreito", "narrow", "↔️", "adjective", "", "A rua é muito estreita.", "The street is very narrow."],
      ["largo", "wide", "📐", "adjective", "", "O rio é muito largo aqui.", "The river is very wide here."],
      ["fundo", "deep", "🌊", "adjective", "", "O lago é muito fundo.", "The lake is very deep."],
      ["leve", "light", "🪶", "adjective", "", "A mala é leve.", "The suitcase is light."],
      ["pesado", "heavy", "🏋️", "adjective", "", "Esta caixa é muito pesada.", "This box is very heavy."],
      ["suave", "soft", "🧴", "adjective", "", "O tecido é muito suave.", "The fabric is very soft."],
      ["áspero", "rough", "🪵", "adjective", "", "A superfície é áspera.", "The surface is rough."],
      ["brilhante", "shiny", "✨", "adjective", "", "O metal está brilhante.", "The metal is shiny."],
      ["escuro", "dark", "🌑", "adjective", "", "O quarto está escuro.", "The room is dark."],
      ["transparente", "transparent", "🔍", "adjective", "", "O vidro é transparente.", "The glass is transparent."],
    ],
  },

  // ---- B2 tier: more abstract, nuanced vocabulary for the upper-intermediate
  // path. Authored to stretch beyond concrete A1–B1 nouns into feelings, work,
  // society, and shades of description. -----------------------------------
  {
    theme: "feelings", tier: "B2", rows: [
      ["o sufoco", "overwhelm", "😮‍💨", "noun", "m", "Sinto um sufoco enorme com tanto trabalho.", "I feel huge overwhelm with so much work."],
      ["a incerteza", "uncertainty", "🌫️", "noun", "f", "Vivemos uma época de incerteza.", "We live in a time of uncertainty."],
      ["o ressentimento", "resentment", "💢", "noun", "m", "Guarda ressentimento desde a discussão.", "He holds resentment since the argument."],
      ["o sossego", "serenity", "🧘", "noun", "m", "Procuro um pouco de sossego.", "I seek a little serenity."],
      ["assoberbado", "overwhelmed", "🥵", "adjective", "", "Estava assoberbado com as notícias.", "He was overwhelmed by the news."],
      ["resignar-se", "to resign oneself", "🤷", "verb", "", "Resignou-se a perder o comboio.", "She resigned herself to missing the train."],
      ["afetuoso", "endearing", "🤗", "adjective", "", "Foi um encontro afetuoso.", "It was a heartwarming encounter."],
      ["a saudade", "longing", "🕰️", "noun", "f", "Sente saudade da sua terra.", "She feels longing for her hometown."],
      ["atarefado", "swamped", "🌊", "adjective", "", "Sinto-me atarefada esta semana.", "I feel swamped this week."],
      ["comovente", "moving", "🥹", "adjective", "", "O discurso foi comovente.", "The speech was moving."],
    ],
  },
  {
    theme: "work-study", tier: "B2", rows: [
      ["a incumbência", "remit / task", "🎯", "noun", "f", "Cumpriu a sua incumbência com eficácia.", "He carried out his remit efficiently."],
      ["o desafio", "challenge", "🧗", "noun", "m", "Aceitou o desafio sem hesitar.", "He took on the challenge without hesitating."],
      ["a contratação", "hiring / recruitment", "📝", "noun", "f", "A contratação foi congelada este ano.", "Hiring was frozen this year."],
      ["o desempenho", "performance", "📈", "noun", "m", "Avaliam o desempenho todos os trimestres.", "They assess performance each quarter."],
      ["delegar", "to delegate", "🤝", "verb", "", "Aprendi a delegar tarefas.", "I learned to delegate tasks."],
      ["o sindicato", "trade union", "✊", "noun", "m", "O sindicato convocou uma reunião.", "The union called a meeting."],
      ["a vaga", "job vacancy", "🪑", "noun", "f", "Há uma vaga na equipa.", "There is a vacancy in the team."],
      ["a liderança", "leadership", "🧭", "noun", "f", "Demonstrou uma grande liderança.", "She showed great leadership."],
      ["polivalente", "versatile", "🧰", "adjective", "", "Procuram um perfil polivalente.", "They want a versatile profile."],
      ["a rentabilidade", "profitability", "📈", "noun", "f", "Analisam a rentabilidade do projeto.", "They analyse the project's profitability."],
    ],
  },
  {
    theme: "town", tier: "B2", rows: [
      ["o vereador", "town councillor", "🧑‍⚖️", "noun", "m", "O vereador apresentou o projeto.", "The councillor presented the project."],
      ["o regulamento", "bylaw", "📋", "noun", "m", "O regulamento proíbe o ruído noturno.", "The bylaw bans night-time noise."],
      ["a urbe", "metropolis", "🌃", "noun", "f", "A urbe nunca dorme.", "The metropolis never sleeps."],
      ["os subúrbios", "city outskirts", "🏙️", "noun", "m", "Vivem nos subúrbios.", "They live on the city outskirts."],
      ["o imóvel", "property", "🏢", "noun", "m", "O imóvel está à venda.", "The building is for sale."],
      ["movimentado", "busy (with traffic)", "🚦", "adjective", "", "É uma avenida muito movimentada.", "It's a very busy avenue."],
      ["as obras", "roadworks / building site", "🚧", "noun", "f", "Há obras em toda a avenida.", "There are roadworks along the whole avenue."],
      ["a vizinhança", "neighbourhood", "🏡", "noun", "f", "A vizinhança é muito tranquila.", "The neighbourhood is very quiet."],
      ["o esgoto", "sewer system", "🚰", "noun", "m", "Renovam os esgotos do bairro.", "They are renovating the neighbourhood's sewer system."],
      ["o trâmite", "paperwork / procedure", "📋", "noun", "m", "Fiz o trâmite online.", "I did the procedure online."],
    ],
  },
  {
    theme: "nature", tier: "B2", rows: [
      ["a encosta", "hillside", "⛰️", "noun", "f", "As casas erguem-se na encosta.", "The houses rise on the hillside."],
      ["o aquecimento", "warming", "🌡️", "noun", "m", "O aquecimento global avança.", "Global warming is advancing."],
      ["a inundação", "flood", "🌊", "noun", "f", "A inundação arrasou os campos.", "The flood devastated the fields."],
      ["o desenvolvimento sustentável", "sustainable development", "♻️", "phrase", "", "Apostam no desenvolvimento sustentável.", "They back sustainable development."],
      ["a biodiversidade", "biodiversity", "🦋", "noun", "f", "A biodiversidade está em perigo.", "Biodiversity is under threat."],
      ["abater", "to fell (trees)", "🪓", "verb", "", "Abateram meio hectare de floresta.", "They felled half a hectare of forest."],
      ["o derrame", "spill", "🛢️", "noun", "m", "O derrame danificou a costa.", "The spill damaged the coast."],
      ["renovável", "renewable", "🔋", "adjective", "", "Usamos energia renovável.", "We use renewable energy."],
      ["o resíduo", "waste", "🗑️", "noun", "m", "Reciclamos todos os resíduos.", "We recycle all the waste."],
      ["preservar", "to preserve", "🌲", "verb", "", "Há que preservar as florestas.", "We must preserve the forests."],
    ],
  },
  {
    theme: "describing", tier: "B2", rows: [
      ["imprescindível", "essential", "❗", "adjective", "", "A tua ajuda é imprescindível.", "Your help is essential."],
      ["chamativo", "striking", "🌟", "adjective", "", "Usa um casaco chamativo.", "She wears a striking coat."],
      ["invulgar", "unusual", "❕", "adjective", "", "Foi uma reação invulgar.", "It was an unusual reaction."],
      ["fiável", "reliable", "✅", "adjective", "", "É uma fonte fiável.", "It's a reliable source."],
      ["escasso", "scarce", "📉", "adjective", "", "A água é escassa ali.", "Water is scarce there."],
      ["surpreendente", "surprising", "😲", "adjective", "", "O resultado foi surpreendente.", "The result was surprising."],
      ["duradouro", "long-lasting", "⏱️", "adjective", "", "Procuramos uma solução duradoura.", "We want a long-lasting solution."],
      ["ambíguo", "ambiguous", "❓", "adjective", "", "A resposta dele foi ambígua.", "His answer was ambiguous."],
      ["chocante", "shocking", "💥", "adjective", "", "As imagens são chocantes.", "The images are shocking."],
      ["matizado", "nuanced", "🎚️", "adjective", "", "Deu uma opinião matizada.", "She gave a nuanced opinion."],
    ],
  },
  // ---- Science & Technology (B2) --------------------------------------
  {
    theme: "science-tech", tier: "B2", rows: [
      ["a investigação", "research", "🔬", "noun", "f", "A investigação durou três anos.", "The research lasted three years."],
      ["o avanço", "breakthrough / advance", "🚀", "noun", "m", "É um avanço científico fundamental.", "It is a key scientific advance."],
      ["o dado", "piece of data", "📊", "noun", "m", "Os dados sustentam a teoria.", "The data support the theory."],
      ["a rede", "network", "🕸️", "noun", "f", "A rede caiu esta manhã.", "The network went down this morning."],
      ["o dispositivo", "device", "📱", "noun", "m", "Sincroniza todos os teus dispositivos.", "Sync all your devices."],
      ["atualizar", "to update", "🔄", "verb", "", "Tens de atualizar a aplicação.", "You have to update the app."],
      ["o ensaio", "trial / test", "🧪", "noun", "m", "O ensaio clínico foi bem-sucedido.", "The clinical trial was successful."],
      ["a pegada", "footprint / trace", "👣", "noun", "f", "Reduzimos a pegada de carbono.", "We are cutting the carbon footprint."],
      ["sem fios", "wireless", "📶", "adjective", "", "Uso uns auscultadores sem fios.", "I use wireless headphones."],
      ["a descoberta", "finding", "💡", "noun", "f", "A descoberta surpreendeu toda a gente.", "The finding surprised everyone."],
    ],
  },
  // ---- Politics & Society (B2) ----------------------------------------
  {
    theme: "politics", tier: "B2", rows: [
      ["o governo", "government", "🏛️", "noun", "m", "O governo anunciou reformas.", "The government announced reforms."],
      ["a lei", "law", "⚖️", "noun", "f", "A nova lei entra em vigor amanhã.", "The new law takes effect tomorrow."],
      ["o direito", "right", "✊", "noun", "m", "É um direito fundamental.", "It is a fundamental right."],
      ["a cidadania", "citizenry", "🧑‍🤝‍🧑", "noun", "f", "A cidadania exige transparência.", "Citizens demand transparency."],
      ["votar", "to vote", "🗳️", "verb", "", "Amanhã vamos votar.", "Tomorrow we are going to vote."],
      ["o partido", "political party", "🚩", "noun", "m", "O partido perdeu as eleições.", "The party lost the election."],
      ["a desigualdade", "inequality", "⚖️", "noun", "f", "Há muita desigualdade económica.", "There is a lot of economic inequality."],
      ["o acordo", "agreement", "🤝", "noun", "m", "Chegaram a um acordo histórico.", "They reached a historic agreement."],
      ["polémico", "controversial", "🔥", "adjective", "", "Foi uma decisão polémica.", "It was a controversial decision."],
      ["a manifestação", "protest / demonstration", "📢", "noun", "f", "Milhares assistiram à manifestação.", "Thousands attended the protest."],
    ],
  },
  // ---- Media & News (B2) ----------------------------------------------
  {
    theme: "media", tier: "B2", rows: [
      ["a notícia", "news item", "📰", "noun", "f", "A notícia espalhou-se depressa.", "The news spread quickly."],
      ["a manchete", "headline", "🗞️", "noun", "f", "A manchete era exagerada.", "The headline was exaggerated."],
      ["a fonte", "source", "🔎", "noun", "f", "Há que citar a fonte.", "You have to cite the source."],
      ["a imprensa", "the press", "📑", "noun", "f", "A imprensa cobriu o evento.", "The press covered the event."],
      ["a reportagem", "report / feature", "🎥", "noun", "f", "Vi uma reportagem sobre o tema.", "I saw a feature on the topic."],
      ["divulgar", "to spread / broadcast", "📡", "verb", "", "Divulgaram o vídeo nas redes.", "They spread the video on social media."],
      ["enviesado", "biased", "↔️", "adjective", "", "O artigo está muito enviesado.", "The article is very biased."],
      ["a conferência de imprensa", "press conference", "🎙️", "phrase", "", "Convocaram uma conferência de imprensa.", "They called a press conference."],
      ["o boato", "hoax / fake news", "🚫", "noun", "m", "Afinal era um boato.", "It turned out to be a hoax."],
      ["verídico", "truthful", "✅", "adjective", "", "Procura informação verídica.", "Look for truthful information."],
    ],
  },
];

function buildVocabulary() {
  const out = [];
  let rank = 1;
  for (const block of [...THEMES, ...EXTRA_THEME_BLOCKS]) {
    for (const [portuguese, english, emoji, pos, gender, exPt, exEn] of block.rows) {
      out.push({
        portuguese,
        english,
        emoji,
        theme: block.theme,
        tier: block.tier,
        part_of_speech: pos,
        gender: gender || "none",
        example_pt: exPt,
        example_en: exEn,
        rank: rank++,
      });
    }
  }
  return out;
}

// Theme registry — drives the browse view's filter chips and section headers.
export const VOCAB_THEMES = [
  { id: "identity", label: "Identity", emoji: "🪪" },
  { id: "family", label: "Family", emoji: "👪" },
  { id: "house", label: "House & Home", emoji: "🏠" },
  { id: "food", label: "Food & Drink", emoji: "🍽️" },
  { id: "body-health", label: "Body & Health", emoji: "🩺" },
  { id: "daily-routine", label: "Daily Routine", emoji: "⏰" },
  { id: "work-study", label: "Work & Study", emoji: "💼" },
  { id: "travel", label: "Travel", emoji: "🧳" },
  { id: "time-weather", label: "Time & Weather", emoji: "🌤️" },
  { id: "shopping", label: "Shopping", emoji: "🛒" },
  { id: "town", label: "Town & Directions", emoji: "🏙️" },
  { id: "leisure", label: "Leisure", emoji: "🎉" },
  { id: "feelings", label: "Feelings", emoji: "😊" },
  { id: "nature", label: "Nature", emoji: "🌳" },
  { id: "describing", label: "Describing Things", emoji: "🎨" },
  { id: "science-tech", label: "Science & Technology", emoji: "🔬" },
  { id: "politics", label: "Politics & Society", emoji: "🏛️" },
  { id: "media", label: "Media & News", emoji: "📰" },
];

export const VOCAB_TIERS = ["A1", "A2", "B1", "B2", "C1", "C2"];

// Flattened, ranked card list — the single source for the view and the seed.
export const VOCABULARY_CARDS = buildVocabulary();
